import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, ImageOff, Pencil, PlusCircle, Trash2 } from 'lucide-react';

import { fetchPaginatedProducts, fetchProductById, deleteProduct, ITEMS_PER_PAGE } from '../AddProduct/api';
import { Product } from '../AddProduct/types';
import { formatCurrency } from '../ViewProduct/utils';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage';
import {
  buildProductFinalColumns,
  getSelectedScript,
  getScriptDeadlineInDays,
  productFinalScreenKey,
} from './productColumns';
import {
  BoardWrapper,
  BoardGrid,
  CreateBoardCard,
  BoardCard,
  BoardCoverArea,
  BoardCoverFallback,
  BoardInfo,
  BoardCode,
  BoardTitle,
  BoardFooter,
  TagBadge
} from './style';

import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../components/ModalConfirmation';

const BOARD_COVER_PALETTE = ['#EDE3B9', '#D9D5EA', '#CBE9DD', '#EFD7D9', '#D0E3F2'];

const ProductFinalsTable: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setFeedback } = React.useContext(FeedbackContext);
  const { refresh, appliedFilters, search, viewMode } = useOutletContext<{
    refresh: boolean;
    appliedFilters: any[];
    search: string;
    viewMode?: 'board' | 'table';
  }>();

  const searchTerm = search || '';
  const otherFilters = appliedFilters;

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch
  } = useInfiniteQuery({
    queryKey: ['products', searchTerm, otherFilters],
    queryFn: ({ pageParam = 1 }) => {
      return fetchPaginatedProducts({
        pageParam,
        search: searchTerm,
        filters: otherFilters.length > 0 ? otherFilters : undefined
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !lastPage.data) return undefined;
      const totalFetched = allPages.length * ITEMS_PER_PAGE;
      if (totalFetched < lastPage.total) {
        return allPages.length + 1;
      }
      return undefined;
    },
  });

  const products = useMemo(
    () => data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.total ?? null;

  useEffect(() => {
    refetch();
  }, [refresh, refetch]);

  const [selectedScriptIds, setSelectedScriptIds] = useState<Record<number, number>>({});

  const handleScriptChange = useCallback((productId: number, scriptId: number) => {
    setSelectedScriptIds(prev => ({ ...prev, [productId]: scriptId }));
  }, []);

  const columns = useMemo(
    () => buildProductFinalColumns(selectedScriptIds, handleScriptChange),
    [selectedScriptIds, handleScriptChange]
  );

  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    productFinalScreenKey,
    columns
  );

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, productId: null as number | null, productName: '' });

  const { mutate: removeProduct } = useMutation({
    mutationFn: (id: number) => deleteProduct(String(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setFeedback({ message: 'Produto excluído com sucesso!', isOpen: true, statusOk: true });
      setDeleteModal({ isOpen: false, productId: null, productName: '' });
    },
    onError: (error: Error) => {
      setFeedback({ message: `Erro ao excluir produto: ${error.message}`, isOpen: true, statusOk: false });
    },
  });

  const handleView = (productId: number) => navigate(`/panel/product-final/${productId}`);
  const handleEdit = (productId: number) => navigate(`/panel/product-final/edit/${productId}`);
  const handleDeleteClick = (productId: number, productName: string) => {
    setDeleteModal({ isOpen: true, productId, productName });
  };
  const handleConfirmDelete = () => {
    if (deleteModal.productId) removeProduct(deleteModal.productId);
  };
  const handleCancelDelete = () => setDeleteModal({ isOpen: false, productId: null, productName: '' });

  const handleDuplicate = async (productId: number) => {
    try {
      const product = await fetchProductById(String(productId));
      const duplicateData = {
        name: `${product.name} - Cópia`,
        norm: product.norm,
        quantity: product.quantity,
        scripts: product.scripts?.map((script: any) => ({
          ...script,
          id: Date.now() + Math.random(),
          name: script.name,
          items: script.items?.map((item: any, index: number) => ({
            ...item,
            id: Date.now() + Math.random() + index,
            order: index + 1,
          })) || []
        })) || []
      };
      navigate('/panel/product-final/create', { state: { duplicateData } });
    } catch (err: any) {
      setFeedback({ message: 'Erro ao copiar produto. Tente novamente.', isOpen: true, statusOk: false });
    }
  };

  if (isLoading) return <div style={{ textAlign: 'center', padding: '2rem' }}>Carregando...</div>;
  if (isError) return <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>Erro ao carregar produtos: {error.message}</div>;

  return (
    <>
      {viewMode === 'board' ? (
        <BoardWrapper>
          <BoardGrid>
            <CreateBoardCard onClick={() => navigate('/panel/product-final/create')}>
              <PlusCircle size={28} />
              <span>Novo produto</span>
            </CreateBoardCard>
            {products.map((product: Product, idx: number) => {
              const selectedScript = getSelectedScript(product, selectedScriptIds);
              const deadline = getScriptDeadlineInDays(selectedScript);
              const cost = selectedScript?.cost || 0;
              const price = cost * 1.3;

              return (
                <BoardCard key={product.id}>
                  <BoardCoverArea
                    $background={BOARD_COVER_PALETTE[idx % BOARD_COVER_PALETTE.length]}
                    onClick={() => handleView(product.id!)}
                  >
                    <BoardCoverFallback>
                      <ImageOff size={28} />
                      <span>Sem imagem</span>
                    </BoardCoverFallback>
                  </BoardCoverArea>
                  <BoardInfo>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <BoardCode>{product.norm || '-'}</BoardCode>
                      <BoardTitle>{product.name || '-'}</BoardTitle>
                    </div>
                    <BoardFooter>
                      {selectedScript && (
                        <TagBadge style={{ color: '#4a2d7a', background: '#f3eefb' }} title={selectedScript.name}>
                          {selectedScript.name}
                        </TagBadge>
                      )}
                      <TagBadge style={{ color: '#1e5c3a', background: '#e8f5ee' }}>
                        Prazo: {deadline} dias
                      </TagBadge>
                      <TagBadge style={{ color: '#0f3f59', background: '#eef6fb' }}>
                        {formatCurrency(price)}
                      </TagBadge>
                    </BoardFooter>
                  </BoardInfo>
                </BoardCard>
              );
            })}
          </BoardGrid>
          {hasNextPage && (
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
              <LoadMoreButton
                currentCount={products.length}
                totalCount={totalCount}
                onLoadMore={() => fetchNextPage()}
                isLoading={isFetchingNextPage}
                hasMore={!!hasNextPage}
                label="produtos"
              />
            </div>
          )}
        </BoardWrapper>
      ) : (
        <StandardDataTable
          columns={columns}
          data={products}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          onRestoreColumns={handleRestoreColumns}
          totalCount={totalCount}
          onLoadMore={() => fetchNextPage()}
          hasMore={!!hasNextPage}
          isLoading={isFetchingNextPage}
          countLabel="produtos"
          exportFileName="produtos-engenheirados"
          emptyMessage="Nenhum produto encontrado."
          rowActions={(product: Product) => [
            { label: 'Ver detalhes', icon: <Eye size={16} />, onClick: () => product.id && handleView(product.id) },
            { label: 'Editar', icon: <Pencil size={16} />, onClick: () => product.id && handleEdit(product.id) },
            { label: 'Copiar', icon: <Copy size={16} />, onClick: () => product.id && handleDuplicate(product.id) },
            { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => product.id && handleDeleteClick(product.id, product.name) },
          ]}
        />
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Confirmar Exclusão"
        message={
          <div>
            <p>Tem certeza que deseja excluir o produto: &quot;{deleteModal.productName}&quot;?</p>
            <p>Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </>
  );
};

export default ProductFinalsTable;
